import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/react';
import { useQueryClient } from '@tanstack/react-query';
import { useOnboardingSseStore } from '../stores/onboarding.js';
import { onboardingQueryKey } from './use-onboarding-state.js';

const RECONNECT_DELAY_MS = 2000;

export function useOnboardingSse(userId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { setConnected, setLastEventId } = useOnboardingSseStore();
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Use a ref for lastEventId to avoid re-triggering the effect on every SSE event
  const lastEventIdRef = useRef(useOnboardingSseStore.getState().lastEventId);

  useEffect(() => {
    let cancelled = false;

    async function connect() {
      if (cancelled) return;

      let token: string | null;
      try {
        token = await getToken();
      } catch {
        return;
      }
      if (!token || cancelled) return;

      const baseUrl = import.meta.env.VITE_API_BASE || '/api';
      const params = new URLSearchParams({ token });
      if (lastEventIdRef.current) params.set('lastEventId', lastEventIdRef.current);

      const es = new EventSource(`${baseUrl}/v1/onboard/stream?${params.toString()}`);
      esRef.current = es;

      es.addEventListener('connected', () => {
        if (!cancelled) setConnected(true);
      });

      const handleEvent = (e: MessageEvent) => {
        if (e.lastEventId) {
          lastEventIdRef.current = e.lastEventId;
          setLastEventId(e.lastEventId);
        }
        // Invalidate to refetch fresh state from GET /state
        void queryClient.invalidateQueries({ queryKey: [...onboardingQueryKey(userId)] });
      };

      es.addEventListener('step_completed', handleEvent);
      es.addEventListener('payment_confirmed', handleEvent);
      es.addEventListener('generation_started', handleEvent);
      es.addEventListener('generation_completed', handleEvent);
      es.addEventListener('generation_failed', handleEvent);

      es.onerror = () => {
        if (!cancelled) setConnected(false);
        es.close();
        esRef.current = null;

        if (!cancelled) {
          void queryClient.invalidateQueries({ queryKey: [...onboardingQueryKey(userId)] });

          reconnectTimerRef.current = setTimeout(() => {
            if (!cancelled) void connect();
          }, RECONNECT_DELAY_MS);
        }
      };
    }

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [userId, getToken, queryClient, setConnected, setLastEventId]);
}
